import React, {useState} from "react";
import {Button, Checkbox, Form, Input, message, Modal, Select} from "antd";
import {useNavigate} from "react-router-dom";
import {EyeInvisibleOutlined, EyeTwoTone} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import LanguageSwitcher from "components/LanguageSwitcher";
import { login, saveUserInfo, removeUserInfo } from "utils/auth";
import { countryCodeOptions } from "constants/countryCodes";
import { UPDATE_ACCOUNT_LANGUAGE } from "graphql/mutations/account/updateAccountLanguage";
import { getStoredLanguage, setStoredLanguage } from "utils/languagePreference";
import { useFetchResourceCodes } from "hooks/useFetchResourceCodes";
import { useFetchWarehouseIds } from "hooks/useFetchWarehouseIds";
import { useFetchDataPortal } from "hooks/useFetchDataPortal";
import { useFetchWatermark } from "hooks/useFetchWatermark";
import { translateGraphQLError } from "utils/graphqlUtils";

const LoginPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const translate = (key: string, options?: any) => t(key, options) as unknown as string;
    const [updateAccountLanguageMutation] = useMutation(UPDATE_ACCOUNT_LANGUAGE);
    const { fetchResourceCodes } = useFetchResourceCodes();
    const { fetchWarehouseIds } = useFetchWarehouseIds();
    const { fetchDataPortal } = useFetchDataPortal();
    const { fetchWatermark } = useFetchWatermark();

    /**
     * 规范化语言代码，只取前两位（如 'zh-CN' -> 'zh'）
     */
    const normalizeLanguage = (lang: string | undefined | null): string | null => {
        if (!lang) return null;
        return lang.toLowerCase().split('-')[0];
    };

    /**
     * 同步用户语言偏好
     * 在登录成功后调用，用于处理 Local Storage 和数据库中的语言偏好同步
     * @param accountLanguage 从后端数据库返回的用户语言偏好（可能为 null）
     * @returns 最终使用的语言（规范化后的）
     */
    const syncLanguagePreference = async (accountLanguage?: string | null): Promise<string | null> => {
        // 规范化数据库中的语言代码
        const normalizedAccountLanguage = normalizeLanguage(accountLanguage);
        
        // 如果数据库中有用户的语言偏好，优先使用数据库的值（保证跨设备一致性）
        if (normalizedAccountLanguage) {
            if (i18n.language !== normalizedAccountLanguage) {
                await i18n.changeLanguage(normalizedAccountLanguage);
            }
            return normalizedAccountLanguage;
        }

        // 如果用户未设置过偏好，但 Local Storage 中有值（登录页选择的语言或浏览器检测），将其同步到数据库
        const storedLanguage = getStoredLanguage();
        if (storedLanguage) {
            // 规范化 localStorage 中的语言代码（可能是 'zh-CN' 来自浏览器检测）
            const normalizedStoredLanguage = normalizeLanguage(storedLanguage);
            if (normalizedStoredLanguage) {
                try {
                    // 保存规范化后的语言代码到数据库
                    await updateAccountLanguageMutation({ variables: { language: normalizedStoredLanguage } });
                } catch (error) {
                    console.error('Failed to persist language preference', error);
                }
                if (i18n.language !== normalizedStoredLanguage) {
                    await i18n.changeLanguage(normalizedStoredLanguage);
                }
                return normalizedStoredLanguage;
            }
        }

        return null;
    };

    const onFinish = async (values: {countryCode: number, phoneOrEmail: string; password: string }) => {
        await attemptLogin(values, false);
    };

    const attemptLogin = async (
        values: {countryCode: number, phoneOrEmail: string; password: string },
        confirmKickout: boolean,
    ) => {
        try {
            setLoading(true);
            // 执行登录请求，成功后返回用户信息（包含从数据库获取的 account.language）
            const result = await login(values.countryCode || 1, values.phoneOrEmail, values.password, confirmKickout);
            const userInfo = result?.userInfo;
            if (!userInfo) {
                // F3：Web 单会话冲突 —— 该端已有其他设备在线，需用户确认踢掉旧设备后重试
                if (result?.requireKickConfirm) {
                    setLoading(false);
                    Modal.confirm({
                        title: t('accountSecurity.session.confirmTitle') as string,
                        content: (
                            <div style={{ whiteSpace: 'pre-line' }}>
                                {t('accountSecurity.session.confirmContent') as string}
                            </div>
                        ),
                        okText: t('accountSecurity.session.confirmOk') as string,
                        cancelText: t('accountSecurity.session.confirmCancel') as string,
                        onOk: () => attemptLogin(values, true),
                    });
                    return;
                }
                if (result?.messageKey === 'errors.business.account.locked') {
                    // 锁定文案由后端按 language header 渲染（含阈值/窗口/剩余分钟）；
                    // 为空说明后端降级（如 Redis 异常）未下发详情，回落通用文案
                    message.error(result.displayMessage || t('errors.business.account.locked'));
                } else {
                    message.error(t('errors.login.failed'));
                }
                setLoading(false);
                return;
            }
            {
                // 登录成功：保存用户信息以便后续 GraphQL mutation 携带 token
                saveUserInfo(userInfo);

                // 仅 PDA 角色（如 RETURNS_STAFF）禁止登录后台：
                // 后端 currentUserResourceCodes / 资源码权限是另一套系统，可能返回非空菜单 codes,
                // 走不到下面的 switch 分支。这里在 fetchResourceCodes 之前硬拦截。
                const deniedRoles = ['RETURNS_STAFF', 'WAREHOUSE_OPERATOR', 'PICKUP_STAFF', 'DELIVERY_DRIVER'];
                if (deniedRoles.includes(userInfo.account.role.name)) {
                    removeUserInfo();
                    message.error(t('errors.permission.currentAccountRoleAccessDenied'));
                    setLoading(false);
                    return;
                }

                // 同步语言偏好：处理 Local Storage 和数据库中的语言偏好
                // userInfo.account.language 是从后端数据库返回的用户语言偏好
                const syncedLanguage = await syncLanguagePreference(userInfo.account.language);
                const normalizedUserInfo = {
                    ...userInfo,
                    account: {
                        ...userInfo.account,
                        language: syncedLanguage ?? userInfo.account.language ?? null,
                    }
                };
                saveUserInfo(normalizedUserInfo);

                let codes: string[] = [];
                try {
                    codes = await fetchResourceCodes();
                } catch (e: any) {
                    console.warn('Fetch resource codes failed', e);
                    message.error(translateGraphQLError(e, 'errors.auth.fetchResourceCodesFailed'));
                }
                fetchWarehouseIds().catch((e: any) => {
                    console.warn('Fetch warehouse ids failed', e);
                    message.error(translateGraphQLError(e, 'errors.auth.getWarehouseIdsFailed'));
                });
                // 登录后拉一次数据门户菜单信号。App 的 useEffect 在未登录时就跑过了
                // （那时 getUserInfo() 为 null，直接 return），登录后不会再触发，所以这里必须补一次。
                // 不用 await：结果进 store 后会自动触发一级菜单重算（见 store/dataPortalStore）
                void fetchDataPortal().catch((e: any) => {
                    console.warn('Fetch data portal failed', e);
                });
                // 水印同理：App 的 useEffect 在未登录时跑过一次就没了，这里补拉，
                // 保证登录后第一屏业务数据渲染出来时水印已经在了（规范要求不留无水印截图窗口）
                void fetchWatermark().catch((e: any) => {
                    console.warn('Fetch watermark failed', e);
                });
                if (codes.length > 0) {
                    navigate('/', {replace: true});
                    return;
                }

                switch (normalizedUserInfo.account.role.name) {
                    case 'WAREHOUSE_MANAGER':
                        navigate('/wms');
                        break;
                    case 'HUB_MANAGER':
                        navigate('/wms');
                        break;
                    case 'HUB_OPERATOR':
                        navigate('/wms');
                        break;
                    case 'SORTING_STAFF':
                        navigate('/wms');
                        break;
                    case 'DSP_MANAGER':
                        navigate('/delivery');
                        break;
                    case 'DSP_OWNER_TEAM':
                        navigate('/delivery/dsp-bill');
                        break;
                    case 'HEADQUARTERS_STAFF':
                        navigate('/central');
                        break;
                    case 'HEADQUARTERS_ADMIN':
                        navigate('/central');
                        break;
                    case 'FINANCIAL_TEAM':
                        // Financial Team 进入结算中心（Central 下的 Settlement 菜单）
                        navigate('/central');
                        break;
                    case 'DSP_FINANCIAL_TEAM':
                        // Financial Team 进入结算中心（Central 下的 Settlement 菜单）
                        navigate('/central');
                        break;
                    case 'INCOME_FINANCIAL_TEAM':
                        // 收入侧财务团队进入结算中心
                        navigate('/central');
                        break;
                    case 'PICKUP_DISPATCHER':
                        navigate('/pickup');
                        break;
                    case 'KAQC':
                        navigate('/qualitycontrol');
                        break;
                    case 'CS':
                        navigate('/qualitycontrol');
                        break;
                    case 'CS_FOLLOW_UP':
                        navigate('/qualitycontrol');
                        break;
                    case 'RISK_CONTROL':
                        // 司机信息审核已迁入客服系统（qualitycontrol），直接导航到该页面本身——
                        // 不能只 navigate('/qualitycontrol')，QualityControlDefaultRoute 对该角色无特判，会兜底跳 podReview（RISK_CONTROL 无权限）
                        navigate('/qualitycontrol/driver-verification');
                        break;
                    case 'TMS_DISPATCHER':
                        saveUserInfo(userInfo);
                        navigate('/tms');
                        break;
                    case 'CONFIG_ADMIN':
                        navigate('/config');
                        break;
                    default:
                        throw new Error('Invalid role');
                }
            }
        } catch (error) {
            console.error(error);
            message.error(t('errors.login.failed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
            <div className="w-full max-w-md px-12 py-16 bg-white rounded-lg shadow-sm">
                <div className="flex justify-end">
                    <LanguageSwitcher/>
                </div>
                <div className="mb-10">
                    <h1 className="text-3xl font-bold text-gray-800 mb-3">{t('login.title')}</h1>
                    <p className="text-gray-500">{t('login.description')}</p>
                </div>

                <Form
                    name="login"
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{ countryCode: 1, remember: true}}
                >
                    <Form.Item
                        label={<span className="text-gray-500">{t('login.phone.label')}</span>}
                    >
                        <div className="flex gap-2">
                            <Form.Item
                                name="countryCode"
                                className="mb-0 w-20"
                                rules={[{ required: true, message: translate('login.phone.required') }]}
                            >
                                <Select
                                    options={countryCodeOptions}
                                    size="large"
                                />
                            </Form.Item>

                            <Form.Item
                                name="phoneOrEmail"
                                className="mb-0 flex-1"
                                rules={[{ required: true, message: translate('login.phone.required') }]}
                            >
                                <Input
                                    placeholder={translate('login.phone.placeholder')}
                                    size="large"
                                    className="rounded"
                                />
                            </Form.Item>
                        </div>
                    </Form.Item>


                    <Form.Item
                        label={<span className="text-gray-500">{t('login.password.label')}</span>}
                        name="password"
                        rules={[{required: true, message: translate('login.password.required')}]}
                    >
                        <Input.Password
                            placeholder={translate('login.password.placeholder')}
                            size="large"
                            className="rounded py-2"
                            iconRender={visible => (visible ? <EyeTwoTone/> : <EyeInvisibleOutlined/>)}
                        />
                    </Form.Item>

                    {
                        false && <div className="flex items-center justify-between mt-6 mb-8">
                            <Form.Item name="remember" valuePropName="checked" noStyle>
                                <Checkbox className="text-gray-700">
                                    <span className="ml-2">{t('login.remember')}</span>
                                </Checkbox>
                            </Form.Item>
                            <Button type="link">
                                {t('login.recover')}
                            </Button>
                        </div>
                    }

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            block
                            loading={loading}
                        >
                            {t('login.submit')}
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </div>
    );
};

export default LoginPage;